// modal-state.ts — Pure decision functions extracted from modal.ts.
//
// The modal hook's DOM-side wrappers (reconcileShellInteractivity, the
// Tab handler inside trapModalFocus) had several intertwined branches
// each computing what the shell / focus target SHOULD be and then
// mutating the DOM in the same step. The branchy mutate-or-not-or-also
// shape is what made the wallaby "stacked modal cleanup" regression
// possible — it's hard to write a regression test for a behaviour
// that's only visible by side effect.
//
// These two functions take the decision in isolation: count + closing →
// shell state, and active + first + last + shiftKey + focusedInside →
// next focus target. modal.ts keeps the DOM mutation as a thin wrapper.

// What the shell SHOULD look like for a given runtime state. The two
// flags are independent on purpose:
//   - `wantDisabled` controls the inert attribute / overflow lock /
//     event suppression. It's true exactly when one or more modal
//     sessions are open.
//   - `wantOpenClass` controls the `<html class="modal-open">` toggle
//     that drives the shell's pre-close transitions. It's true only
//     when a session is open AND we're not in the closing transition
//     yet (the shell starts returning to its rest position the moment
//     the user clicks Escape, before the panel finishes fading).
export type ShellState = {
  wantDisabled: boolean;
  wantOpenClass: boolean;
};

export type ShellInput = {
  openCount: number;
  closing: boolean;
};

export const computeShellState = ({
  openCount,
  closing,
}: ShellInput): ShellState => {
  const wantDisabled = openCount > 0;
  // wantOpenClass implies wantDisabled (you can't be "closing" without
  // first being "open"), so the conjunction here mirrors the existing
  // reconcile branch in modal.ts. Keeping the conjunction explicit
  // rather than `wantDisabled && !closing` lets the call site pattern-
  // match on the input fields directly.
  const wantOpenClass = openCount > 0 && !closing;
  return { wantDisabled, wantOpenClass };
};

// Tab routing inside a focus-trapped modal. The four-way branching
// inside trapModalFocus is the kind of code that silently breaks the
// moment someone reorders the conditions, so the rule is hoisted out
// where a property test can pin it.
//
// Returns the element the caller should focus, or null when the
// caller should let the browser's default tab order proceed (which
// happens when the focused element is somewhere in the middle of the
// trap and the user is tabbing forward or backward as normal).
export type FocusInput = {
  active: Element | null;
  first: Element;
  last: Element;
  shiftKey: boolean;
  // True when the active element is inside the trap container at all
  // — set by the caller from `root.contains(active)`. When false and
  // shift+tabbing from first, there's nothing to wrap to so we still
  // return last; when false and tab-from-last, we return first.
  focusedInside: boolean;
};

export const computeFocusTarget = ({
  active,
  first,
  last,
  shiftKey,
  focusedInside,
}: FocusInput): Element | null => {
  // Forward tab: when active === last, wrap to first. This is the
  // canonical end-of-trap case. We also wrap when the active element
  // isn't inside the trap at all (focusedInside === false), so a
  // forward tab from outside lands at first rather than escaping.
  if (!shiftKey) {
    if (active === last) return first;
    if (!focusedInside) return first;
    return null;
  }
  // Shift-tab: when active === first, wrap to last. Same out-of-trap
  // logic in reverse — shift-tab from outside lands at last.
  if (active === first) return last;
  if (!focusedInside) return last;
  return null;
};
